import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CheckCircle2, Clock, Eye, FileText, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { FilterBar, type FilterConfig } from '@/components/ui/FilterBar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Paginator } from '@/components/ui/Paginator'
import { useQuiz, useQuizAttempts, useDeleteQuizAttempt } from '@/hooks/useQuizzes'
import { useUsersByIds } from '@/hooks/useUsers'
import { QuizAttemptModel } from '@/models/quiz.model'
import { QuizAttemptQueryParams, QuizAttemptStatus } from '@/types/quizzes'
import { paths } from '@/routes/path'
import { useAuth } from '@/contexts/AuthContext'
import { labelForQuizAttemptStatus } from '@/utils/quiz'
import BackButton from '@/components/ui/BackButton'

type AttemptFilters = {
  status?: QuizAttemptStatus | ''
  search?: string
  learnerId?: string
}

const attemptFilterConfig: FilterConfig[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search attempts',
    placeholder: 'Search by learner name, ID, or status',
  },
  {
    key: 'status',
    type: 'select',
    label: 'Status',
    options: [
      { label: 'All', value: '' },
      { label: 'In progress', value: 'in_progress' },
      { label: 'Submitted', value: 'submitted' },
      { label: 'Graded', value: 'graded' },
      { label: 'Expired', value: 'expired' },
    ],
  },
  {
    key: 'learnerId',
    type: 'text',
    label: 'Learner',
    placeholder: 'Exact learner ID',
  },
]

export function QuizAttemptsPage() {
  const { quizId } = useParams<{ quizId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [filters, setFilters] = useState<AttemptFilters>({})
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 })
  const [attemptToDelete, setAttemptToDelete] = useState<QuizAttemptModel | null>(null)

  const { data: quiz, isLoading: isQuizLoading, error: quizError } = useQuiz(quizId)
  const serverFilters = useMemo<QuizAttemptQueryParams>(() => {
    const payload: QuizAttemptQueryParams = {
      page: pagination.page,
      perPage: pagination.perPage,
    }
    if (filters.status) {
      payload.status = filters.status
    }
    const learnerId = filters.learnerId?.trim()
    if (learnerId) {
      payload.learnerId = learnerId
    }
    return payload
  }, [filters.status, filters.learnerId, pagination.page, pagination.perPage])
  const { data: attemptsData, isLoading: isAttemptsLoading } = useQuizAttempts(quizId, serverFilters)
  const deleteAttempt = useDeleteQuizAttempt(quizId)

  const attempts = (attemptsData?.attempts ?? []) as QuizAttemptModel[]
  const attemptsMeta = attemptsData?.meta
  const learnerIds = useMemo(() => attempts.map((attempt) => attempt.learnerId), [attempts])
  const { data: learnersById = {} } = useUsersByIds(learnerIds)

  const resolveLearnerName = useCallback(
    (attempt: QuizAttemptModel) => {
      const inlineName = attempt.learnerName?.trim()
      if (inlineName) return inlineName
      if (attempt.learner?.email) {
        return attempt.learner.email
      }
      const directoryEntry = attempt.learnerId ? learnersById[attempt.learnerId] : undefined
      const directoryName = directoryEntry?.fullName?.trim()
      if (directoryName) return directoryName
      if (directoryEntry?.email) return directoryEntry.email
      return attempt.learnerId
    },
    [learnersById],
  )

  const filteredAttempts = useMemo(() => {
    const searchTerm = filters.search?.trim().toLowerCase()
    const status = filters.status
    const learnerIdFilter = filters.learnerId?.trim().toLowerCase()
    return attempts.filter((attempt) => {
      const matchesStatus = !status || attempt.status === status
      const matchesLearner =
        !learnerIdFilter || attempt.learnerId.toLowerCase() === learnerIdFilter
      const learnerNameTerm = resolveLearnerName(attempt)?.toLowerCase() ?? ''
      const matchesSearch =
        !searchTerm ||
        attempt.learnerId.toLowerCase().includes(searchTerm) ||
        attempt.status.toLowerCase().includes(searchTerm) ||
        learnerNameTerm.includes(searchTerm)
      return matchesStatus && matchesSearch && matchesLearner
    })
  }, [attempts, filters.search, filters.status, filters.learnerId, resolveLearnerName])

  const handleFiltersChange = (values: Record<string, any>) => {
    setFilters({
      search: values.search ?? '',
      status: (values.status as AttemptFilters['status']) ?? '',
      learnerId: values.learnerId ?? '',
    })
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleDeleteAttempt = async () => {
    if (!attemptToDelete) return
    await deleteAttempt.mutateAsync(attemptToDelete.id)
    toast.success('Attempt deleted.')
    setAttemptToDelete(null)
  }

  const handlePageChange = (page: number) => {
    const nextPage = Math.max(1, page)
    if (attemptsMeta) {
      const maxPage = Math.max(1, Math.ceil(attemptsMeta.total / attemptsMeta.perPage))
      setPagination((prev) => ({ ...prev, page: Math.min(nextPage, maxPage) }))
      return
    }
    setPagination((prev) => ({ ...prev, page: nextPage }))
  }

  const handlePerPageChange = (perPage: number) => {
    const sanitized = Math.max(1, perPage)
    setPagination({ page: 1, perPage: sanitized })
  }

  if (!quizId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Quiz not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not identify the quiz you are trying to review.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isAttemptsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (quizError || !quiz) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Unable to load quiz</CardTitle>
            <CardDescription className="text-sm text-red-600">
              The quiz may have been removed or you may not have permission to view attempts.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const canDeleteAttempts = user?.role === 'admin'

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <BackButton/>
            <p className="text-sm font-medium text-red-600">Quiz attempts</p>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
          <p className="max-w-3xl text-gray-600">
            Monitor learner progress, review submissions, and update scores. Manual grading is
            available for written answers and file uploads.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(paths.app.quizzes.detail.build(quiz.id))}>
          Back to quiz setup
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Attempt overview</CardTitle>
          <CardDescription>
            Use the filters to focus on pending grading or follow up with specific learners.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FilterBar filters={attemptFilterConfig} onFiltersChange={handleFiltersChange} />

          {filteredAttempts.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <FileText className="h-10 w-10 text-gray-300" />
              <p className="text-lg font-semibold text-gray-700">No attempts yet</p>
              <p className="max-w-md text-sm text-gray-500">
                Learners have not started this quiz or your filters may be hiding results.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAttempts.map((attempt) => (
                <Card key={attempt.id} className="border border-gray-200">
                  <CardContent className="flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">Attempt {attempt.attempt}</Badge>
                        <Badge variant={badgeVariantForStatus(attempt.status)}>
                          {labelForQuizAttemptStatus(attempt.status)}
                        </Badge>
                        {attempt.autoScore !== null && (
                          <Badge variant="secondary">
                            Auto score: {attempt.autoScore?.toFixed(2)}
                          </Badge>
                        )}
                        {attempt.manualScore !== null && (
                          <Badge variant="secondary">
                            Manual score: {attempt.manualScore?.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                      <dl className="grid gap-x-6 gap-y-2 text-xs text-gray-500 sm:grid-cols-2">
                        <div>
                          <dt className="font-medium text-gray-600">Learner</dt>
                          <dd className="text-sm font-semibold text-gray-900">
                            {resolveLearnerName(attempt)}
                          </dd>
                          {/* <p className="font-mono text-xs text-gray-500">ID: {attempt.learnerId}</p> */}
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">Started</dt>
                          <dd>{attempt.startedAt ? new Date(attempt.startedAt).toLocaleString() : 'Unknown'}</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">Submitted</dt>
                          <dd>{attempt.submittedAt ? new Date(attempt.submittedAt).toLocaleString() : 'Not yet submitted'}</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-600">Duration</dt>
                          <dd>
                            {attempt.durationSeconds
                              ? `${Math.round((attempt.durationSeconds ?? 0) / 60)} min`
                              : 'In progress'}
                          </dd>
                        </div>
                      </dl>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() =>
                          navigate(paths.app.quizzes.attempts.detail.build(quiz.id, attempt.id))
                        }
                      >
                        <Eye className="h-4 w-4" />
                        View details
                      </Button>
                      {(attempt.status === 'submitted' || attempt.status === 'graded') && (
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={() =>
                            navigate(paths.app.quizzes.attempts.detail.build(quiz.id, attempt.id))
                          }
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Grade attempt
                        </Button>
                      )}
                      {canDeleteAttempts && (
                        <Button
                          variant="ghost"
                          className="gap-2 text-red-600 hover:text-red-700"
                          onClick={() => setAttemptToDelete(attempt)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {attemptsMeta && (
            <Paginator
              meta={attemptsMeta}
              page={pagination.page}
              perPage={pagination.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              isPreviousData={isAttemptsLoading}
            />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(attemptToDelete)}
        title="Delete attempt?"
        description={
          attemptToDelete
            ? `This will permanently remove attempt ${attemptToDelete.attempt} by ${resolveLearnerName(attemptToDelete)} (ID: ${attemptToDelete.learnerId}).`
            : ''
        }
        confirmLabel="Delete attempt"
        confirmTone="destructive"
        onCancel={() => setAttemptToDelete(null)}
        onConfirm={handleDeleteAttempt}
      />
    </div>
  )
}

function badgeVariantForStatus(status: QuizAttemptStatus): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'graded':
      return 'default'
    case 'submitted':
      return 'secondary'
    case 'in_progress':
      return 'outline'
    case 'expired':
      return 'destructive'
    default:
      return 'outline'
  }
}
